import { FC, useEffect, useState } from 'react';
import { useCondition } from '../ExperimentsProvider';
import BaseAvatarPanel from './BaseAvatarPanel';
import FutureSelfIntroPanel from './FutureSelfIntroPanel';
import { PersonalityQuestionnaire } from './Questionnaire/PersonalityQuestionnaire';
import { ProgressIndicator } from './Questionnaire/ProgressIndicator';
import { type AvatarFeedbackData } from './Questionnaire/AvatarFeedback';
import { type AppearanceInput } from './BaseAvatarPanel';
import { useExperimentSpeculativeData } from '../../hooks/useExperimentSpeculativeData';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { TEAME_223_AVATAR_UPDATED_EVENT } from './useTEAME223SpeculativeAvatarData';
import { TEAME_223_FLAG_ID, TEAME_223_TREATMENT } from './constants';

const DEFAULT_FUTURE_SELF_AVATAR_SPECULATIVE_DATA: {
  image?: string;
  has_seen?: boolean;
  avatarFeedback?: AvatarFeedbackData;
} = {};

interface FutureSelfModalProps {
  isVisible?: boolean;
  onVisibleChange?: (isOpen: boolean) => void;
}

export const FutureSelfModal: FC<FutureSelfModalProps> = ({ isVisible = false, onVisibleChange }) => {
  const user = useAppSelector(selectUser);
  const variantName = useCondition(TEAME_223_FLAG_ID);
  const isFutureSelvesEnabled = variantName === TEAME_223_TREATMENT;
  const userNodeId = user?.id ? `UserNode:${user.id}` : undefined;
  const {
    data: avatarSpeculativeData,
    hydrated: hasHydratedFutureSelfData,
    saveSpeculativeData,
    setData: setAvatarSpeculativeData,
  } = useExperimentSpeculativeData<{
    image?: string;
    has_seen?: boolean;
    avatarFeedback?: AvatarFeedbackData;
  }>({
    publicUserId: user?.sub,
    userNodeId,
    flagName: TEAME_223_FLAG_ID,
    variantName,
    defaultValues: DEFAULT_FUTURE_SELF_AVATAR_SPECULATIVE_DATA,
    enabled: isFutureSelvesEnabled && Boolean(user?.sub),
  });
  const hasSeenAvatar = Boolean(avatarSpeculativeData.has_seen || avatarSpeculativeData.image);
  const [step, setStep] = useState<
    'intro' | 'avatar' | 'big5' | 'values' | 'interests' | 'futureSelves' | 'avatarFeedback'
  >('intro');
  const [avatarImageUrl, setAvatarImageUrl] = useState('');
  const [avatarAppearance, setAvatarAppearance] = useState<AppearanceInput | null>(null);
  const isOpen = isFutureSelvesEnabled && (isVisible || (hasHydratedFutureSelfData && !hasSeenAvatar));

  const closeModal = (savedImageBase64?: string, avatarFeedback?: AvatarFeedbackData) => {
    setAvatarSpeculativeData(prev => ({
      ...prev,
      ...(savedImageBase64 ? { image: savedImageBase64 } : {}),
      ...(avatarFeedback ? { avatarFeedback } : {}),
      has_seen: true,
    }));
    onVisibleChange?.(false);
    window.dispatchEvent(new Event(TEAME_223_AVATAR_UPDATED_EVENT));
  };

  const handleClose = () => {
    if (userNodeId) {
      void saveSpeculativeData({
        userNodeId,
        flagName: TEAME_223_FLAG_ID,
        variantName,
        payload: { has_seen: true },
      });
    }
    closeModal();
  };

  useEffect(() => {
    if (!isOpen) {
      setStep('intro');
      setAvatarImageUrl('');
      setAvatarAppearance(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/50"
      style={{ zIndex: 100002 }}
      onClick={handleClose}
    >
      <div
        className="tw-relative tw-w-[90vw] tw-h-[90vh] tw-overflow-hidden tw-bg-white tw-rounded-3xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="tw-h-full tw-flex tw-flex-col">
          <div className="tw-flex tw-items-start tw-gap-2 tw-pr-4 tw-bg-white tw-border-b tw-border-gray-200">
            <div className="tw-flex-1 tw-min-w-0">
              <ProgressIndicator currentStep={step} className="tw-border-b-0" />
            </div>
            <button
              onClick={handleClose}
              className="tw-mt-3 tw-shrink-0 tw-text-2xl tw-leading-none tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-gray-600 hover:tw-text-gray-900"
              aria-label="Close future self modal"
            >
              ✕
            </button>
          </div>

          {step === 'intro' ? (
            <FutureSelfIntroPanel onNoThanks={handleClose} onAppearance={() => setStep('avatar')} />
          ) : step === 'avatar' ? (
            <BaseAvatarPanel
              onAvatarGenerated={(imageUrl, appearance) => {
                setAvatarImageUrl(imageUrl);
                setAvatarAppearance(appearance);
              }}
              onContinue={() => setStep('big5')}
            />
          ) : (
            <PersonalityQuestionnaire
              avatarImage={avatarImageUrl}
              avatarAppearance={avatarAppearance}
              step={step}
              setStep={setStep}
              onComplete={closeModal}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FutureSelfModal;
